/**
 * SoundManager - Audio System for T-Stacker
 * Manages Web Audio Context, ZzFX integration, and sound settings
 */

import * as SoundEffects from './SoundEffects';
import { ZZFX } from 'zzfx';

export class SoundManager {
    private static instance: SoundManager;
    private masterVolume: number = 0.5;
    private sfxVolume: number = 0.5;
    private enabled: boolean = true;
    private warningCooldown: number = 0;

    private constructor() {}

    public static getInstance(): SoundManager {
        if (!SoundManager.instance) {
            SoundManager.instance = new SoundManager();
        }
        return SoundManager.instance;
    }

    public init(): void {
        // ZzFX handles audio initialization internally on first play
        // This is a no-op but kept for API compatibility
    }

    public resume(): void {
        // Resume ZzFX AudioContext if it exists and is suspended
        if (ZZFX.audioContext && ZZFX.audioContext.state === 'suspended') {
            ZZFX.audioContext.resume();
        }
    }

    public setMasterVolume(volume: number): void {
        this.masterVolume = Math.max(0, Math.min(1, volume));
    }

    public setSfxVolume(volume: number): void {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
    }

    public toggleSound(): boolean {
        this.enabled = !this.enabled;
        return this.enabled;
    }

    public isEnabled(): boolean {
        return this.enabled;
    }

    private play(config: any): void {
        if (!this.enabled || this.sfxVolume === 0) return;

        this.resume();

        // Apply volume settings
        const adjustedConfig = {
            ...config,
            volume: config.volume * this.sfxVolume * this.masterVolume
        };

        SoundEffects.playZzFX(adjustedConfig);
    }

    // ==================== UI SOUNDS ====================

    public playHover(): void {
        this.play(SoundEffects.uiSounds.hover);
    }

    public playClick(): void {
        this.play(SoundEffects.uiSounds.click);
    }

    public playBack(): void {
        this.play(SoundEffects.uiSounds.back);
    }

    public playMenuOpen(): void {
        this.play(SoundEffects.uiSounds.menuOpen);
    }

    // ==================== GAME SOUNDS ====================

    public playGrab(): void {
        this.play(SoundEffects.gameSounds.grab);
    }

    public playRelease(): void {
        this.play(SoundEffects.gameSounds.release);
    }

    public playImpact(): void {
        this.play(SoundEffects.gameSounds.impact);
    }

    public playThrow(): void {
        this.play(SoundEffects.gameSounds.throw);
    }

    // ==================== SCORING SOUNDS ====================

    public playTJunction(): void {
        this.play(SoundEffects.scoringSounds.tJunction);
    }

    public playScoreUp(): void {
        this.play(SoundEffects.scoringSounds.scoreUp);
    }

    // ==================== GAME STATE SOUNDS ====================

    public playGameStart(): void {
        this.play(SoundEffects.gameStateSounds.gameStart);
    }

    public playSettling(): void {
        this.play(SoundEffects.gameStateSounds.settling);
    }

    public playGameOver(): void {
        this.play(SoundEffects.gameStateSounds.gameOver);
    }

    // ==================== TIMER SOUNDS ====================

    public playWarning(): void {
        // Play warning every second, but with cooldown to prevent spam
        const now = Date.now();
        if (now - this.warningCooldown < 500) return;

        this.warningCooldown = now;
        this.play(SoundEffects.timerSounds.warning);
    }
}

export const soundManager = SoundManager.getInstance();
